import { Typography } from 'antd/lib';
import { Tooltip } from '@atvlibs/atvdesignsystem';
import {
	DeviceWithCamerasDTO,
	DeployActionEnum,
	DeviceStatusEnum,
	SoftwareNameEnum,
} from 'api/api_code';
import clsx from 'clsx';
import { Icon } from 'components/UI/Icon/Icon';
import { IconNames } from 'components/UI/Icon/icons';
import { snakeCaseToCamelCase } from 'helpers/utils';
import { isProBroadcastSelector } from 'stores/camera.store';
import { usePairingContext } from 'hooks/usePairingContext';
import { useRouter } from 'next/router';
import { Dispatch, memo, SetStateAction } from 'react';
import styles from './CameraList.module.css';
import useTranslation from 'next-translate/useTranslation';

const CameraStatus = memo(
	({
		camera,
		setOpenLicensingModal,
	}: {
		camera: DeviceWithCamerasDTO;
		setOpenLicensingModal: Dispatch<SetStateAction<boolean>>;
	}) => {
		const router = useRouter();
		const { t } = useTranslation('cameras');
		const { pairedDeviceUUID } = usePairingContext();
		const { status, uuid, software_installing } = camera;
		const caseUpgrading =
			software_installing?.find(
				(item) => item.version?.software?.name === SoftwareNameEnum.Automatictv
			)?.action === DeployActionEnum.Upgrade;
		const caseInstalling =
			software_installing?.find(
				(item) => item.version?.software?.name === SoftwareNameEnum.Automatictv
			)?.action === DeployActionEnum.Install;

		const cameraLinkClickHandler = () => {
			if (status === DeviceStatusEnum.Unlicensed) {
				pairedDeviceUUID.current = uuid;
				setOpenLicensingModal(true);
			} else if (status === DeviceStatusEnum.NotConfigured) {
				router.push(
					{
						pathname: '/camera/[device_uuid]/settings',
						query: { device_uuid: uuid, previous: `/camera/${uuid}/details` },
					},
					`/camera/${uuid}/settings`
				);
			}
		};

		if (
			status === DeviceStatusEnum.Unlicensed ||
			status === DeviceStatusEnum.NotConfigured
		) {
			return (
				<Typography.Link
					data-testid="camera_status"
					onClick={(event) => {
						cameraLinkClickHandler();
						event.stopPropagation();
					}}
					disabled={caseUpgrading || caseInstalling}
				>
					{t(`status.values.${snakeCaseToCamelCase(status)}`)}
				</Typography.Link>
			);
		}

		const setTextStyles = (status: DeviceStatusEnum) =>
			clsx({
				[styles.soft]: status === DeviceStatusEnum.Offline,
				[styles.velvet]: status === DeviceStatusEnum.Recording,
				[styles.gold]: status === DeviceStatusEnum.Unlicensed,
				[styles.gold]: status === DeviceStatusEnum.NotConfigured,
				[styles.gold]: status === DeviceStatusEnum.CameraUnavailable,
			});

		return (
			<Typography.Text
				data-testid="camera_status"
				className={clsx(styles.statusText, setTextStyles(status))}
			>
				{status === DeviceStatusEnum.Recording && (
					<Icon component={IconNames.RECORDING} className="mr-05" />
				)}
				{t(`status.values.${snakeCaseToCamelCase(status)}`)}
				{status === DeviceStatusEnum.CameraUnavailable && (
					<Tooltip
						title={t(
							isProBroadcastSelector({
								cameraConfig: camera,
							} as Parameters<typeof isProBroadcastSelector>[0])
								? 'tooltip.disconnectedPro'
								: 'tooltip.disconnected'
						)}
						className={styles.tooltipHidden}
						customBodyStyle={{ maxWidth: '432px' }}
					>
						<Icon component={IconNames.INFO} size={24} />
					</Tooltip>
				)}
			</Typography.Text>
		);
	}
);

CameraStatus.displayName = 'CameraStatus';

export { CameraStatus };
